
import { Component, Vue, Prop } from "vue-property-decorator";
import { TreeNode } from "@/types/tree";
import {
  calculateElementPositionOnCanvas,
  listenForPositionChangesAndGetNewPosition,
  ElementPositionOnCanvas,
  getWrapperStyle,
  getOutlineStyle
} from "@/futurelayer/canvas/canvas-controls/element-positioning";
import { documentStore } from "@/store/store";
import { findNode } from "@/lib/tree/tree";
import { handleAddAnother, handleMovePrevious, handleMoveNext, isContainerThatSupportsRepeatingChildren } from "@/futurelayer/canvas/canvas-controls/grid-and-link-wrapper-detection";
import Icon from "@/components/UI/Icon.vue";

@Component({
  components: {
    Icon
  }
})
export default class FutureLayerRepeatableItemOutlineAndControls extends Vue {
  @Prop()
  element!: TreeNode;

  elementPositionOnCanvas: ElementPositionOnCanvas = false;
  cleanupListeners: () => void = () => {};

  mounted() {
    this.elementPositionOnCanvas = calculateElementPositionOnCanvas(
      this.element
    );

    this.cleanupListeners = listenForPositionChangesAndGetNewPosition(
      this.element,
      (newPosition: ElementPositionOnCanvas) => {
        this.elementPositionOnCanvas = newPosition;
      }
    );
  }

  beforeDestroy() {
    this.cleanupListeners();
  }

  get cssStyleForWrapper(): Record<string, string> {
    return getWrapperStyle(this.elementPositionOnCanvas);
  }

  get cssStyleForOutline(): Record<string, string> {
    return getOutlineStyle(this.elementPositionOnCanvas);
  }

  get cssStyleForControls(): Record<string, string> {
    if (!this.elementPositionOnCanvas) {
      return { display: "none" };
    }

    return {
      left: `calc(${this.elementPositionOnCanvas.width} + 2px)`,
      transform: "translateX(-100%)"
    };
  }

  handleRemove() {
    documentStore.deleteElement({ nodeId: this.element.id });
  }

  handleAddAnother() {
    handleAddAnother(this.element);
  }

  handleMoveLeft() {
    handleMovePrevious(this.element);
  }

  handleMoveRight() {
    handleMoveNext(this.element);
  }

  get parent(): TreeNode | undefined {
    return findNode(
      documentStore.document.tree,
      this.element._parentId
    );
  }

  get isRepeatableChildButNotOnlyChild() {
    const parent = this.parent;

    if (!parent) return false;
    if (!this.isRepeatableParent(parent)) return false;
    if (parent.children.length === 1) return false;

    return true;
  }

  get isFirstChild(): boolean {
    const parent = this.parent;
    if (!parent) return false;
    if (!this.isRepeatableParent(parent)) return false;
    return parent.children[0]?.id === this.element.id;
  }

  get isLastChild(): boolean {
    const parent = this.parent;
    if (!parent) return false;
    if (!this.isRepeatableParent(parent)) return false;
    return parent.children[parent.children.length - 1]?.id === this.element.id;
  }

  isRepeatableParent(parent: TreeNode): boolean {
    return isContainerThatSupportsRepeatingChildren(parent);
  }
}
